import treffas from "src/theme/treffas";

const { colors, typography, radius, shadow, button: buttonTokens } = treffas;

export const treffasButtonBaseSx = {
  boxShadow: "none",
  textTransform: "none",
  fontFamily: typography.fontFamily.heading,
  fontWeight: typography.fontWeight.medium,
  lineHeight: typography.lineHeight.normal,
  borderRadius: radius.md,
  "&:focus": {
    outline: "none",
  },
  "&:focus-visible": {
    boxShadow: shadow.focus,
  },
};

export const treffasButtonSizeSx = {
  sm: {
    minHeight: buttonTokens.height.sm,
    fontSize: typography.fontSize.md,
    padding: `0 ${buttonTokens.paddingX.sm}`,
  },
  md: {
    minHeight: buttonTokens.height.md,
    fontSize: typography.fontSize.base,
    padding: `0 ${buttonTokens.paddingX.md}`,
  },
  lg: {
    minHeight: buttonTokens.height.lg,
    fontSize: typography.fontSize.lg,
    padding: `0 ${buttonTokens.paddingX.lg}`,
  },
};

/** @type {Record<string, object>} */
export const treffasButtonVariantSx = {
  primary: {
    color: colors.text.inverse,
    backgroundColor: colors.brand.primary,
    border: `1px solid ${colors.brand.primary}`,
    "&:hover": {
      backgroundColor: colors.brand.primaryHover,
      borderColor: colors.brand.primaryHover,
    },
    "&:active": {
      backgroundColor: colors.brand.primaryHover,
      borderColor: colors.brand.primaryHover,
    },
    "&.Mui-disabled": {
      color: colors.text.inverse,
      backgroundColor: colors.gray[400],
      borderColor: colors.gray[400],
    },
  },
  secondary: {
    color: colors.brand.navy,
    backgroundColor: colors.background.default,
    border: `1px solid ${colors.border.strong}`,
    "&:hover": {
      backgroundColor: colors.background.subtle,
      borderColor: colors.border.strong,
    },
    "&:active": {
      backgroundColor: colors.gray[100],
    },
    "&.Mui-disabled": {
      color: colors.text.disabled,
      backgroundColor: colors.background.default,
      borderColor: colors.border.default,
    },
  },
  ghost: {
    color: colors.brand.primary,
    backgroundColor: "transparent",
    border: "1px solid transparent",
    "&:hover": {
      backgroundColor: colors.brand.primarySoft,
    },
    "&:active": {
      backgroundColor: colors.brand.primarySoft,
    },
    "&.Mui-disabled": {
      color: colors.text.disabled,
      backgroundColor: "transparent",
    },
  },
  danger: {
    color: colors.text.inverse,
    backgroundColor: colors.semantic.error,
    border: `1px solid ${colors.semantic.error}`,
    "&:hover": {
      backgroundColor: "#d42626",
      borderColor: "#d42626",
    },
    "&:active": {
      backgroundColor: "#c12222",
      borderColor: "#c12222",
    },
    "&.Mui-disabled": {
      color: colors.text.inverse,
      backgroundColor: colors.gray[400],
      borderColor: colors.gray[400],
    },
  },
  dark: {
    color: colors.text.inverse,
    backgroundColor: colors.brand.navy,
    border: `1px solid ${colors.brand.navy}`,
    "&:hover": {
      backgroundColor: colors.gray[900],
      borderColor: colors.gray[900],
    },
    "&:active": {
      backgroundColor: colors.gray[900],
      borderColor: colors.gray[900],
    },
    "&.Mui-disabled": {
      color: colors.text.disabled,
      backgroundColor: colors.gray[300],
      borderColor: colors.gray[300],
    },
  },
};

/**
 * Legacy hover-only styles (spread on MUI Button with existing base colors).
 * Prefer <TreffasButton variant="dark" /> for new code.
 */
export const treffasButtonHoverLegacySx = {
  "&:hover": {
    color: colors.text.inverse,
    backgroundColor: colors.gray[900],
    borderColor: colors.gray[900],
  },
  "&:active": {
    color: colors.text.inverse,
    backgroundColor: colors.gray[900],
    borderColor: colors.gray[900],
  },
};

/**
 * @param {"primary"|"secondary"|"ghost"|"danger"|"dark"} variant
 * @param {"sm"|"md"|"lg"} size
 */
export function getTreffasButtonSx(variant = "primary", size = "md") {
  return [
    treffasButtonBaseSx,
    treffasButtonSizeSx[size] || treffasButtonSizeSx.md,
    treffasButtonVariantSx[variant] || treffasButtonVariantSx.primary,
  ];
}
